import React, { useState } from 'react';
import { View, ScrollView, StyleSheet, Text, Pressable, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaZones, PaprikaShadows } from '../../../styles/theme';
import { PantryLocation } from '../../../utils/pantryLocation';
import { ZONE_META } from '../zoneMeta';
import { PantryIcon } from '../PantryIcon';
import { FoodTile } from '../FoodTile';
import { usePantryName } from '../usePantryName';
import { FreshnessPill } from '../FreshnessPill';
import { UrgencyBadge } from '../UrgencyBadge';
import { PantryItemView } from '../../../redux/pantrySelectors';
import Feather from '@expo/vector-icons/Feather';
import { isCookCandidate } from '../../../services/pantryFreshnessService';

interface Props {
  zone: PantryLocation;
  items: PantryItemView[];
  onItem: (itemId: string) => void;
  onPhoto: () => void;
  onAdd: () => void;
  onCook: (itemId: string) => void;
  isDragging?: string | undefined; // itemId currently being dragged
  draggingOver?: boolean; // whether this zone is the drag target
  justMoved?: string | null; // itemId that just landed (for flash animation)
  onItemPointerDown?: (e: any, view: PantryItemView) => void; // drag start handler
}

export function DesktopZoneColumn({
  zone,
  items,
  onItem,
  onPhoto,
  onAdd,
  onCook,
  isDragging,
  draggingOver,
  justMoved,
  onItemPointerDown,
}: Props) {
  const { t } = useTranslation();
  const pantryName = usePantryName();
  const zoneColors = PaprikaZones[zone];
  const zoneMeta = ZONE_META[zone];
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

  const urgentCount = items.filter((v) => v.state === 'urgent' || v.state === 'review').length;

  // Calculate border and shadow for drag target highlight
  const overRingColor = `${zoneColors.accent}33`;
  const columnBorderColor = draggingOver ? zoneColors.accent : `${zoneColors.accent}2a`;
  const columnShadow = draggingOver
    ? {
        shadowColor: zoneColors.accent,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.2,
        shadowRadius: 3,
      }
    : PaprikaShadows.card;

  // RN-web strips unknown props like `data-zone`; `dataSet` is the supported
  // passthrough that renders as data-* attributes for the drag hit-test.
  const dataZoneProps =
    Platform.OS === 'web' ? ({ dataSet: { zone } } as Record<string, unknown>) : {};

  return (
    <View
      {...dataZoneProps}
      style={[
        styles.column,
        {
          borderColor: columnBorderColor,
          ...columnShadow,
        },
      ]}
    >
      {/* Material header: tint + large object-icon watermark + chip/badge + name/count */}
      <LinearGradient
        colors={[zoneColors.gradStart, zoneColors.gradEnd]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0.34, y: 1 }}
        style={styles.header}
      >
        {/* Large faint object-icon watermark ("grande icône-objet en filigrane") */}
        <View style={styles.watermark} pointerEvents="none">
          <PantryIcon icon={zoneMeta.icon} size={92} color={zoneColors.accent} />
        </View>

        {/* Top row: icon chip + urgency badge */}
        <View style={styles.headerTopRow}>
          <View
            style={[
              styles.headerIcon,
              { backgroundColor: Paprika.glassWhite },
            ]}
          >
            <PantryIcon icon={zoneMeta.icon} size={20} color={zoneColors.accent} />
          </View>

          <View style={{ marginLeft: 'auto' }}>
            <UrgencyBadge count={urgentCount} />
          </View>
        </View>

        {/* Zone name and count (inside the tint, like the spec) */}
        <View style={styles.headerInfo}>
          <Text style={[styles.zoneName, { color: Paprika.textPrimary }]}>
            {t(zoneMeta.labelKey)}
          </Text>
          <Text style={[styles.zoneCount, { color: zoneColors.accent }]}>
            {t('pantry.itemsCount', { count: items.length })}
          </Text>
        </View>
      </LinearGradient>

      {/* Items list (scrollable) */}
      <ScrollView style={styles.itemsList} contentContainerStyle={styles.itemsContent}>
        {items.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyText}>{t('pantry.zoneEmptyDesktop')}</Text>
          </View>
        ) : (
          items.map((view) => {
            const isLanded = justMoved === view.item.id;
            const isDraggingThis = isDragging === view.item.id;

            return (
              <Pressable
                key={view.item.id}
                onPress={() => {
                  if (!isDragging) {
                    onItem(view.item.id);
                  }
                }}
                onPointerDown={(e) => {
                  onItemPointerDown?.(e, view);
                }}
                onPointerEnter={() => {
                  if (!isDragging) setHoveredItemId(view.item.id);
                }}
                onPointerLeave={() => {
                  if (!isDragging) setHoveredItemId(null);
                }}
                style={[
                  styles.itemRow,
                  hoveredItemId === view.item.id && !isDragging && styles.itemRowHovered,
                  isLanded && [
                    styles.itemRowLanded,
                    { backgroundColor: `${zoneColors.accent}14` },
                  ],
                  isDraggingThis && styles.itemRowDragging,
                ]}
              >
              <FoodTile
                category={view.item.category}
                name={view.item.name}
                size={32}
                radius={9}
              />

              <View style={styles.itemInfo}>
                <Text style={styles.itemName} numberOfLines={1}>
                  {pantryName(view.item)}
                </Text>
                <FreshnessPill state={view.state} days={view.days} compact={true} />
              </View>

                {/* Hover actions */}
                {hoveredItemId === view.item.id && !isDragging ? (
                  <View style={styles.itemActions}>
                    {isCookCandidate(view.item) && (
                      <Pressable
                        style={[styles.actionButton, styles.cookButton]}
                        onPress={(e) => {
                          e.stopPropagation?.();
                          onCook(view.item.id);
                        }}
                      >
                        <PantryIcon icon={{ lib: 'mci', name: 'fire' }} size={14} color={Paprika.toastText} />
                      </Pressable>
                    )}
                    <Pressable
                      style={[styles.actionButton, styles.menuButton]}
                      onPress={(e) => {
                        e.stopPropagation?.();
                        onItem(view.item.id);
                      }}
                    >
                      <Feather name="more-horizontal" size={14} color={Paprika.textSecondary} />
                    </Pressable>
                  </View>
                ) : (
                  <View
                    style={[
                      styles.stateDot,
                      { backgroundColor: StateColors[view.state] || Paprika.textMuted },
                    ]}
                  />
                )}
              </Pressable>
            );
          })
        )}
      </ScrollView>

      {/* Footer buttons */}
      <View style={[styles.footer, { borderTopColor: `${zoneColors.accent}18` }]}>
        <Pressable
          style={[styles.footerButton, { backgroundColor: zoneColors.accent }]}
          onPress={onPhoto}
        >
          <Feather name="camera" size={15} color="#fff" style={{ alignSelf: 'center' }} />
          <Text style={styles.footerButtonText} numberOfLines={1}>
            {t('pantry.add.photograph')}
          </Text>
        </Pressable>
        <Pressable
          style={[
            styles.footerButton,
            {
              backgroundColor: Paprika.surfaceLowest,
              borderColor: `${zoneColors.accent}88`,
              borderWidth: 1,
              borderStyle: 'dashed',
            },
          ]}
          onPress={onAdd}
        >
          <Feather name="plus" size={15} color={zoneColors.accent} />
          <Text style={[styles.footerButtonText, { color: zoneColors.accent }]} numberOfLines={1}>
            {t('pantry.add.addManual')}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

// State colors matching freshness states
const StateColors: Record<string, string> = {
  urgent: '#d32f2f',
  soon: '#f57c00',
  fresh: '#388e3c',
  stable: '#1565c0',
  review: '#7b1fa2',
};

const styles = StyleSheet.create({
  column: {
    flex: 1,
    minWidth: 0,
    borderRadius: 16,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: Paprika.border,
    backgroundColor: Paprika.surfaceLowest,
    display: 'flex',
    flexDirection: 'column',
    ...PaprikaShadows.card,
  },
  header: {
    paddingTop: 15,
    paddingHorizontal: 15,
    paddingBottom: 14,
    position: 'relative',
    overflow: 'hidden',
  },
  watermark: {
    position: 'absolute',
    right: -12,
    bottom: -20,
    opacity: 0.12,
    transform: [{ rotate: '-8deg' }],
  },
  headerTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
  },
  headerIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerInfo: {
    marginTop: 12,
    position: 'relative',
    zIndex: 1,
  },
  zoneName: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 19,
    letterSpacing: -0.01,
    lineHeight: 24,
    marginBottom: 4,
  },
  zoneCount: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    fontWeight: '500',
  },
  itemsList: {
    flex: 1,
  },
  itemsContent: {
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 12,
  },
  emptyText: {
    fontFamily: 'Inter-Regular',
    fontSize: 11.5,
    color: Paprika.textMuted,
    textAlign: 'center',
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 2,
  },
  itemRowHovered: {
    backgroundColor: Paprika.surfaceHighest,
  },
  itemRowDragging: {
    opacity: 0.35,
  },
  itemRowLanded: {
    // Landed flash animation applied via inline backgroundColor
  },
  itemInfo: {
    flex: 1,
    minWidth: 0,
    marginLeft: 10,
  },
  itemName: {
    fontFamily: 'Inter-Medium',
    fontSize: 12.5,
    fontWeight: '500',
    color: Paprika.textPrimary,
    marginBottom: 4,
  },
  itemActions: {
    flexDirection: 'row',
    gap: 5,
  },
  actionButton: {
    width: 28,
    height: 28,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cookButton: {
    backgroundColor: Paprika.accent,
  },
  menuButton: {
    backgroundColor: Paprika.surfaceLowest,
    borderWidth: 1,
    borderColor: Paprika.border,
  },
  stateDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    marginLeft: 8,
  },
  footer: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderTopWidth: 1,
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
  },
  footerButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    paddingVertical: 9,
    borderRadius: 10,
  },
  footerButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
    textAlign: 'center',
  },
});
